import React, { useRef, useState } from 'react';
import PageTitle from '../components/SEO/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useCV } from '../hooks/useCV';
import TemplateOptions from '../components/CV/TemplateOptions';
import CVForm from '../components/CV/CVForm';
import CVPreview from '../components/CV/CVPreview';
import SectionReorder from '../components/CV/SectionReorder';
import LoginModal from '../components/Auth/LoginModal';
import html2pdf from 'html2pdf.js';

const CVCreator = () => {
    const { currentUser } = useAuth();
    const previewRef = useRef(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [generating, setGenerating] = useState(false);
    
    const {
        cvData,
        template,
        setTemplate,
        color,
        setColor,
        font,
        setFont,
        loading,
        updatePersonal,
        updateSummary,
        updateSkills,
        updateCertifications,
        updateLanguages,
        education,
        addEducation,
        updateEducation,
        removeEducation,
        experience,
        addExperience,
        updateExperience,
        removeExperience,
        resetToDefaults,
        sectionOrder,
        moveSectionUp,
        moveSectionDown
    } = useCV();

    const handleDownloadPDF = async () => {
        if (!currentUser) {
            setShowLoginModal(true);
            return;
        }

        const element = document.getElementById('cv-preview-content');
        if (!element) return;

        setGenerating(true);

        try {
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `CV_${cvData.personal.name.replace(/\s/g, '_') || 'CV'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { 
                    scale: 2, 
                    useCORS: true,
                    logging: false,
                    letterRendering: true,
                    width: 794,
                    height: element.scrollHeight,
                    windowWidth: 794,
                },
                jsPDF: { 
                    unit: 'in', 
                    format: 'a4', 
                    orientation: 'portrait' 
                }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading...</span>
            </div>
        );
    }

    const handlers = {
        updatePersonal,
        updateSummary,
        updateSkills,
        updateCertifications,
        updateLanguages,
        education,
        addEducation,
        updateEducation,
        removeEducation,
        experience,
        addExperience,
        updateExperience,
        removeExperience
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <PageTitle 
                title="Free CV Builder" 
                description="Create a free professional CV online with our easy-to-use CV builder tool. Download as PDF for job applications."
            />
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-2">Professional CV Builder</h1>
                <p className="text-gray-600">Create and download your professional CV as PDF</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Column - Form */}
                <div className="lg:w-1/2">
                    <TemplateOptions
                        template={template}
                        setTemplate={setTemplate}
                        color={color}
                        setColor={setColor}
                        font={font}
                        setFont={setFont}
                    />
                    
                    <SectionReorder
                        sectionOrder={sectionOrder}
                        onMoveUp={moveSectionUp}
                        onMoveDown={moveSectionDown}
                    />
                    
                    <CVForm cvData={cvData} handlers={handlers} />
                    
                    <div className="flex gap-4 mt-6">
                        <button
                            onClick={resetToDefaults}
                            className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-undo-alt"></i> Reset to Defaults
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            disabled={generating}
                            className={`flex-1 font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 ${
                                generating
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white hover:shadow-lg'
                            }`}
                        >
                            {generating ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Generating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-download"></i> Download PDF
                                </>
                            )}
                        </button>
                    </div>
                    {generating && (
                        <p className="text-xs text-gray-400 text-center mt-2">
                            Please wait while we generate your CV...
                        </p>
                    )}
                </div>

                {/* Right Column - Preview */}
                <div className="lg:w-1/2">
                    <div className="sticky top-24">
                        <div className="bg-gray-100 rounded-xl p-4">
                            <h3 className="font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                                <i className="fas fa-eye text-gradpath-green"></i> Live Preview
                            </h3>
                            <div className="max-h-[80vh] overflow-y-auto">
                                <div id="cv-preview-content" ref={previewRef}>
                                    <CVPreview
                                        cvData={cvData}
                                        template={template}
                                        color={color}
                                        font={font}
                                        sectionOrder={sectionOrder}
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-2 text-center">
                                {generating ? 'Generating PDF...' : 'Ready to download'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Login Modal */}
            <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </div>
    );
};

export default CVCreator;